import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import {
  useSaveAiPromptsMutation,
  useLazyGetDefaultAiPromptQuery,
  type ModelAiPrompt,
} from '@app/api/AiPrompts';
import { FRONT_WARNING, SUCCESS, SUCCESS_MESSAGE } from '@app/constants';
import { AI_PROMPT_DESCRIPTIONS } from '@app/pages/AiPromptsSettings/constants';
import { useAiPromptsSettingsEffects } from '@app/pages/AiPromptsSettings/hooks/useAiPromptsSettingsEffects';
import type { TAiPromptsFormValues } from '@app/pages/AiPromptsSettings/types';
import { useConfirmNew } from '@app/providers/NewConfirmProvider/useConfirmNew';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

export const useAiPromptsSettings = () => {
  const [form] = Form.useForm<TAiPromptsFormValues>();
  const { showNotification } = useNotification();
  const { show: showConfirm } = useConfirmNew();

  const { data, isDataLoading, ActiveBranchId } = useAiPromptsSettingsEffects({
    form,
  });

  const [saveAiPrompts, { isLoading: isSaving }] = useSaveAiPromptsMutation();
  const [getDefaultAiPrompt] = useLazyGetDefaultAiPromptQuery();
  const [restoringKey, setRestoringKey] = useState<string | null>(null);

  const prompts = useMemo(
    () =>
      (data ?? [])
        .filter((prompt): prompt is ModelAiPrompt & { Key: string } =>
          Boolean(prompt.Key)
        )
        .sort((a, b) => a.Key.localeCompare(b.Key)),
    [data]
  );

  const fields = useMemo(
    () =>
      prompts.map(({ Key, Title }) => ({
        key: Key,
        title: Title ?? '',
        description: AI_PROMPT_DESCRIPTIONS[Key],
      })),
    [prompts]
  );

  const handleFinishForm = async (values: TAiPromptsFormValues) => {
    if (!ActiveBranchId) {
      showNotification(
        'Произошла ошибка при определении идентификатора филиала',
        FRONT_WARNING
      );
      return;
    }

    if (prompts.length === 0) {
      showNotification(
        'Не удалось загрузить настройки ИИ промптов, попробуйте обновить страницу',
        FRONT_WARNING
      );
      return;
    }

    const promptsData: ModelAiPrompt[] = prompts.map((prompt) => ({
      ...prompt,
      Content: values[prompt.Key] ?? '',
    }));

    try {
      await saveAiPrompts({ ActiveBranchId, data: promptsData }).unwrap();
      showNotification(SUCCESS_MESSAGE, SUCCESS);
    } catch (saveError) {
      showNotification(handleCatchError(saveError));
    }
  };

  const applyDefaultPrompt = useCallback(
    async (key: string) => {
      if (!ActiveBranchId) return;

      setRestoringKey(key);
      try {
        const content = await getDefaultAiPrompt({
          ActiveBranchId,
          promptKey: key,
        }).unwrap();
        form.setFieldValue(key, content);
      } catch (restoreError) {
        showNotification(handleCatchError(restoreError));
      } finally {
        setRestoringKey(null);
      }
    },
    [ActiveBranchId, form, getDefaultAiPrompt, showNotification]
  );

  const handleRestoreDefaultPrompt = useCallback(
    (key: string) => {
      const currentValue = form.getFieldValue(key);

      if (currentValue) {
        showConfirm({
          title: 'Восстановить промпт по умолчанию?',
          message:
            'Текущий текст промпта будет заменён значением по умолчанию.',
          confirmText: 'Заменить',
          cancelText: 'Отмена',
          dataTestId: 'restore-default-prompt',
          onConfirm: () => applyDefaultPrompt(key),
        });
        return;
      }

      applyDefaultPrompt(key);
    },
    [applyDefaultPrompt, form, showConfirm]
  );

  return {
    form,
    fields,
    handleFinishForm,
    isDataLoading,
    isSaving,
    restoringKey,
    handleRestoreDefaultPrompt,
  };
};
